// Rendered-bitmap cache shared across component mounts. Page components get
// remounted constantly (Virtuoso scroll-seek exit, zoom re-layout, canvas
// culling of embedded previews, scrolling back) and every mount gets a fresh
// pooled PDFPage entity whose bitmap$ starts at null — without this cache
// each remount flashes the loading skeleton even for a page that was just on
// screen. The cached bitmap is shown CSS-scaled until the fresh render lands.
//
// Per page+role the cache keeps a small set of resolution VARIANTS (one per
// zoom level the user actually rendered), so revisiting a zoom level shows
// the matching bitmap instantly instead of an upscaled blur. Entries carry a
// temperature: a hit count with exponential time decay. Pages the user keeps
// coming back to stay hot and survive eviction; one-off renders cool down and
// are evicted first. The budget is total pixels, not entry count, because a
// retina page at max zoom is ~50MB while a thumbnail is a few hundred KB.
//
// Evicted bitmaps are NOT closed: a still-mounted page may display them; GC
// reclaims them once unreferenced.

const PDF_PAGE_BITMAP_CACHE_MAX_PIXELS = 96_000_000;
const PDF_PAGE_BITMAP_HEAT_HALF_LIFE_MS = 5 * 60_000;
const PDF_PAGE_BITMAP_MAX_VARIANTS_PER_PAGE = 3;
// React re-renders of a mounted page read the cache on every commit; without
// damping that pumps heat by render count instead of user attention.
const PDF_PAGE_BITMAP_TOUCH_DAMPING_MS = 1_000;

export type PdfPageBitmapRole = 'page' | 'thumbnail' | 'embedded';

interface PdfPageBitmapVariant {
  bitmap: ImageBitmap;
  pixels: number;
  /** Decayed hit count as of `touchedAt`. */
  heat: number;
  touchedAt: number;
}

export class PdfPageBitmapCache {
  /** pageKey -> variantKey (`WxH`) -> variant */
  private readonly pages = new Map<string, Map<string, PdfPageBitmapVariant>>();
  private totalPixels = 0;

  constructor(
    private readonly maxPixels = PDF_PAGE_BITMAP_CACHE_MAX_PIXELS,
    private readonly heatHalfLifeMs = PDF_PAGE_BITMAP_HEAT_HALF_LIFE_MS,
    private readonly maxVariantsPerPage = PDF_PAGE_BITMAP_MAX_VARIANTS_PER_PAGE,
    private readonly touchDampingMs = PDF_PAGE_BITMAP_TOUCH_DAMPING_MS
  ) {}

  /**
   * Best cached bitmap for a page at a target render width (device pixels):
   * the smallest variant that still downscales sharply (width >= target),
   * otherwise the largest one below the target.
   */
  read(
    pdfId: string,
    pageNum: number,
    role: PdfPageBitmapRole,
    targetWidth: number,
    now = Date.now()
  ): ImageBitmap | null {
    const variants = this.pages.get(pageKey(pdfId, pageNum, role));
    if (!variants) return null;

    let best: PdfPageBitmapVariant | null = null;
    for (const variant of variants.values()) {
      if (!best) {
        best = variant;
        continue;
      }
      const bestCovers = best.bitmap.width >= targetWidth;
      const variantCovers = variant.bitmap.width >= targetWidth;
      const isBetter = variantCovers
        ? !bestCovers || variant.bitmap.width < best.bitmap.width
        : !bestCovers && variant.bitmap.width > best.bitmap.width;
      if (isBetter) {
        best = variant;
      }
    }
    if (!best) return null;

    this.touch(best, now);
    return best.bitmap;
  }

  write(
    pdfId: string,
    pageNum: number,
    role: PdfPageBitmapRole,
    bitmap: ImageBitmap,
    now = Date.now()
  ) {
    const key = pageKey(pdfId, pageNum, role);
    let variants = this.pages.get(key);
    if (!variants) {
      variants = new Map();
      this.pages.set(key, variants);
    }

    const variantKey = `${bitmap.width}x${bitmap.height}`;
    const existing = variants.get(variantKey);
    if (existing?.bitmap === bitmap) {
      this.touch(existing, now);
      return;
    }

    const pixels = bitmap.width * bitmap.height;
    if (existing) {
      // Same dimensions, fresh render — inherit the heat, swap the bitmap.
      this.totalPixels -= existing.pixels;
      existing.bitmap = bitmap;
      existing.pixels = pixels;
      this.totalPixels += pixels;
      this.touch(existing, now);
    } else {
      const variant: PdfPageBitmapVariant = {
        bitmap,
        pixels,
        heat: 1,
        touchedAt: now,
      };
      variants.set(variantKey, variant);
      this.totalPixels += pixels;
    }

    // Per-page variant cap: zoom jitter must not collect unbounded
    // resolutions of the same page; drop the coldest sibling.
    while (variants.size > this.maxVariantsPerPage) {
      const coldest = this.coldestIn(variants, variantKey, now);
      if (!coldest) break;
      this.totalPixels -= variants.get(coldest)?.pixels ?? 0;
      variants.delete(coldest);
    }

    this.evictOverBudget(key, variantKey, now);
  }

  get pixels() {
    return this.totalPixels;
  }

  private heatAt(variant: PdfPageBitmapVariant, now: number) {
    return (
      variant.heat *
      Math.pow(0.5, Math.max(0, now - variant.touchedAt) / this.heatHalfLifeMs)
    );
  }

  private touch(variant: PdfPageBitmapVariant, now: number) {
    if (now - variant.touchedAt < this.touchDampingMs) return;
    variant.heat = this.heatAt(variant, now) + 1;
    variant.touchedAt = now;
  }

  private coldestIn(
    variants: Map<string, PdfPageBitmapVariant>,
    excludeVariantKey: string,
    now: number
  ) {
    let coldestKey: string | null = null;
    let coldestHeat = Infinity;
    for (const [variantKey, variant] of variants) {
      if (variantKey === excludeVariantKey) continue;
      const heat = this.heatAt(variant, now);
      if (heat < coldestHeat) {
        coldestHeat = heat;
        coldestKey = variantKey;
      }
    }
    return coldestKey;
  }

  private evictOverBudget(
    keepPageKey: string,
    keepVariantKey: string,
    now: number
  ) {
    // Evict the globally coldest variants until under budget, but always keep
    // the just-written entry so a single oversized page bitmap still survives
    // its own remount.
    while (this.totalPixels > this.maxPixels) {
      let coldestPageKey: string | null = null;
      let coldestVariantKey: string | null = null;
      let coldestHeat = Infinity;
      for (const [pk, variants] of this.pages) {
        for (const [vk, variant] of variants) {
          if (pk === keepPageKey && vk === keepVariantKey) continue;
          const heat = this.heatAt(variant, now);
          if (heat < coldestHeat) {
            coldestHeat = heat;
            coldestPageKey = pk;
            coldestVariantKey = vk;
          }
        }
      }
      if (coldestPageKey === null || coldestVariantKey === null) break;

      const variants = this.pages.get(coldestPageKey);
      if (!variants) break;
      this.totalPixels -= variants.get(coldestVariantKey)?.pixels ?? 0;
      variants.delete(coldestVariantKey);
      if (variants.size === 0) {
        this.pages.delete(coldestPageKey);
      }
    }
  }
}

function pageKey(pdfId: string, pageNum: number, role: PdfPageBitmapRole) {
  return `${pdfId}:${pageNum}:${role}`;
}

const sharedCache = new PdfPageBitmapCache();

export function readPdfPageBitmapCache(
  pdfId: string,
  pageNum: number,
  role: PdfPageBitmapRole,
  targetWidth: number
): ImageBitmap | null {
  return sharedCache.read(pdfId, pageNum, role, targetWidth);
}

export function writePdfPageBitmapCache(
  pdfId: string,
  pageNum: number,
  role: PdfPageBitmapRole,
  bitmap: ImageBitmap
) {
  sharedCache.write(pdfId, pageNum, role, bitmap);
}
